/**
 * HomePage.tsx
 *
 * Desktop: video full-width + info + strip thumbnail sotto
 * Mobile: Reel Mode – feed verticale swipe
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { MobileReel } from '@/components/MobileReel';
import { YouTubePlayer } from '@/components/YouTubePlayer';
import { fetchTrailers, extractYouTubeId, getYouTubeThumbnail } from '@/lib/api';
import type { Trailer } from '@/lib/api';

const ArrowLeft = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ArrowRight = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

export function HomePage() {
  const [trailers, setTrailers] = useState<Trailer[]>([]);
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const stripRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchTrailers(60)
      .then(setTrailers)
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Errore nel caricamento dei trailer');
      })
      .finally(() => setLoading(false));
  }, []);

  const goTo = useCallback((index: number) => {
    setCurrent(Math.max(0, Math.min(trailers.length - 1, index)));
  }, [trailers.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goTo(current - 1);
      if (e.key === 'ArrowRight') goTo(current + 1);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [current, goTo]);

  // Scrolla la thumbnail attiva nella strip
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const thumb = strip.querySelector<HTMLElement>(`[data-index="${current}"]`);
    if (thumb) thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [current]);

  if (loading) {
    return <main><div className="trailer-loading"><div className="spinner" /></div></main>;
  }

  if (error) {
    return (
      <main>
        <div className="trailer-error" role="alert">
          <p>Impossibile caricare i trailer.</p>
          <p style={{ fontSize: '0.8rem', opacity: 0.6 }}>{error}</p>
        </div>
      </main>
    );
  }

  if (trailers.length === 0) {
    return <main><div className="trailer-loading"><p>Nessun trailer disponibile.</p></div></main>;
  }

  const activeTrailer = trailers[current];
  const videoId = activeTrailer.data.video_embed
    ? extractYouTubeId(activeTrailer.data.video_embed)
    : null;

  return (
    <main>
      {/* ── DESKTOP ── */}
      <div className="cinema-layout">

        {/* Player full-width */}
        <div className="cinema-player-area">
          {videoId ? (
            <YouTubePlayer
              key={videoId}
              videoId={videoId}
              title={activeTrailer.title}
              onEnded={() => goTo(current + 1)}
            />
          ) : (
            <div className="cinema-player-wrapper cinema-player-empty">
              <p>Embed non disponibile</p>
            </div>
          )}
        </div>

        {/* Info + navigazione */}
        <div className="cinema-info-bar">
          <div className="cinema-info-bar-left">
            <h1 className="cinema-info-title">{activeTrailer.title}</h1>
            <div className="cinema-info-meta">
              <span>{formatDate(activeTrailer.post_date)}</span>
            </div>
            {activeTrailer.data.excerpt && (
              <p className="cinema-info-excerpt">{activeTrailer.data.excerpt}</p>
            )}
          </div>

          <div className="cinema-info-bar-right">
            <button
              className="cinema-nav-btn"
              onClick={() => goTo(current - 1)}
              disabled={current === 0}
              aria-label="Trailer precedente (←)"
            >
              <ArrowLeft />
            </button>
            <span className="cinema-counter">{current + 1} / {trailers.length}</span>
            <button
              className="cinema-nav-btn"
              onClick={() => goTo(current + 1)}
              disabled={current === trailers.length - 1}
              aria-label="Trailer successivo (→)"
            >
              <ArrowRight />
            </button>
          </div>
        </div>

        {/* Strip thumbnail */}
        <div className="cinema-strip" ref={stripRef}>
          {trailers.map((trailer, i) => {
            const vid = trailer.data.video_embed ? extractYouTubeId(trailer.data.video_embed) : null;
            const thumb = vid ? getYouTubeThumbnail(vid) : (trailer.data.thumbnail ?? '');
            return (
              <button
                key={trailer.post_id}
                className={`cinema-strip-item${i === current ? ' active' : ''}`}
                data-index={i}
                onClick={() => goTo(i)}
                aria-label={trailer.title}
                aria-current={i === current}
              >
                {thumb && <img src={thumb} alt={trailer.title} loading="lazy" />}
                <span className="cinema-strip-title">{trailer.title}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── MOBILE ── */}
      <div className="reel-layout-wrapper">
        <MobileReel trailers={trailers} />
      </div>
    </main>
  );
}
